import { ArrowUpRight, ExternalLink, FileText } from "lucide-react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import InvoiceDetailModal from "@panel/shared/invoice/components/invoice-detail-modal";
import { INVOICE_STATUS_LABEL,  INVOICE_STATUS_TONE } from "@src/utility/constants/panel/common";
import { formatCurrency } from "@src/utility/helpers/utils";
import { formatDate } from "@src/utility/helpers/date";

// `invoicesTab` is the page's usePublisherInvoices() — fetched at page level so the
// tab label can carry the count.
const InvoicesTab = ({ invoicesTab }) => {
    const { invoices, loading, stats, detailTarget, openInvoice, closeInvoice, isSelfPayout } = invoicesTab;

    const columns = [
        {
            Header  : "Invoice",
            accessor: "invoice_number",
            // The stored tax invoice is one click away from the row itself; the
            // action column's modal is for everything around it.
            Cell    : ({ value, row }) => (row.invoice_url ? (
                <a
                    href={row.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.invoice_meta?.original_name || row.invoice_meta?.name || "Tax invoice"}
                    className="inline-flex items-center gap-1.5 font-medium text-gray-900 hover:text-primary hover:underline">
                    {value}
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            ) : <span className="font-medium text-gray-900">{value}</span>)
        },
        { Header: "Brand", accessor: "account_name", Cell: ({ value }) => <span className="text-gray-700">{value}</span> },
        { Header: "Period", accessor: "payout_period", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value) : "—"}</span> },
        // The debit note is the document the brand settles with Nook Pay, so it only
        // exists under managed payouts. Under self payout the invoice above is the
        // whole chain — a second column for it would just repeat itself.
        ...(isSelfPayout ? [] : [
            {
                Header  : "Debit note",
                accessor: "debit_note_number",
                Cell    : ({ value, row }) => (
                    <Link to={`/brands/details/${row.account_id}`} className="font-medium text-accent hover:underline">
                        {value}
                    </Link>
                )
            }
        ]),
        { Header: "Status", accessor: "status", Cell: ({ value }) => <StatusChip tone={INVOICE_STATUS_TONE[value] || "neutral"} label={INVOICE_STATUS_LABEL[value] || value} /> },
        { Header: "Reference", accessor: "payment_reference", Cell: ({ value }) => <span className="text-gray-700">{value || "—"}</span> },
        { Header: "Amount", accessor: "amount", Cell: ({ value }) => <span className="font-medium text-gray-900">{formatCurrency(value)}</span> },
        {
            Header  : "Action",
            accessor: "_id",
            Cell    : ({ row }) => (
                <button
                    type="button"
                    onClick={() => openInvoice(row)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                    View invoice
                    <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
            )
        }
    ];

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted">Invoices</p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-gray-900">{stats.total_invoices ?? 0}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted">Outstanding</p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-gray-900">{formatCurrency(stats.outstanding)}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted">Paid to date</p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-success">{stats.paid_to_date ? formatCurrency(stats.paid_to_date) : "—"}</p>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-gray-200 px-5 py-4">
                    <FileText className="h-4 w-4 text-muted" />
                    <h2 className="text-sm font-semibold text-gray-900">Invoices ({invoices.length})</h2>
                </div>
                <Datatable
                    columns={columns}
                    data={invoices}
                    loading={loading}
                    showPagination={false}
                    customNoData={<span className="text-sm text-muted">No invoices for this publisher yet.</span>} />
            </div>

            <InvoiceDetailModal
                open={Boolean(detailTarget)}
                onClose={closeInvoice}
                invoice={detailTarget} />
        </div>
    );
};

InvoicesTab.propTypes = {
    invoicesTab: PropTypes.object.isRequired
};

export default InvoicesTab;
